import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink, Menu, X } from 'lucide-react';
import { professionalData } from '../professionalData';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/research', label: 'Research & Innovation' },
    { path: '/services', label: 'Clinical Services' },
    { path: '/insights', label: 'Insights' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <nav
      className="glass-nav"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        transition: 'all 0.3s ease'
      }}
      data-testid="main-navigation"
    >
      <div className="container" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '1.5rem 2rem'
      }}>
        {/* Logo / Name */}
        <Link 
          to="/" 
          style={{ 
            fontFamily: 'var(--font-heading)', 
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'var(--primary)',
            textDecoration: 'none',
            border: 'none',
            zIndex: 1001
          }}
          data-testid="logo-link"
        >
          Isha Rastogi
        </Link>

        {/* Desktop Navigation Links */}
        <div 
          className="desktop-nav"
          style={{ 
            display: 'flex', 
            gap: '2.5rem',
            alignItems: 'center'
          }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.95rem',
                fontWeight: 500,
                color: location.pathname === link.path ? 'var(--accent)' : 'var(--text-main)',
                textDecoration: 'none',
                borderBottom: location.pathname === link.path ? '2px solid var(--accent)' : 'none',
                paddingBottom: '0.25rem',
                transition: 'all 0.3s ease'
              }}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
            </Link>
          ))}
          
          {/* Modern Minds External Link */}
          <a
            href={professionalData.clinic.website}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              fontSize: '0.95rem'
            }}
            data-testid="modern-minds-link"
          >
            Modern Minds
            <ExternalLink size={16} />
          </a>
        </div>

        {/* Mobile Hamburger Menu Button */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.5rem',
            color: 'var(--primary)',
            zIndex: 1001
          }}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            background: 'linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2rem',
            padding: '2rem',
            animation: 'fadeIn 0.3s ease'
          }}
          data-testid="mobile-menu"
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '2rem',
                fontWeight: 600,
                color: location.pathname === link.path ? 'var(--accent)' : 'var(--primary)',
                textDecoration: 'none',
                transition: 'all 0.3s ease'
              }}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
            </Link>
          ))}
          
          <a
            href={professionalData.clinic.website}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '1rem 2rem',
              fontSize: '1.125rem',
              marginTop: '1rem'
            }}
            data-testid="mobile-modern-minds-link"
          >
            Modern Minds
            <ExternalLink size={20} />
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
